import Form from 'antd/lib/form';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Switch from 'antd/lib/switch';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import { selectFixRequestsAutoMergeUserCreatedPRsSettings } from '@/selectors/selectors';
import { MergeType } from '@/types';
import { Select } from 'antd/lib';
import { Option } from 'antd/lib/mentions';
import { useWatch } from 'antd/es/form/Form';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useFixSettingsModal } from './useFixSettingsModal';
import FixSettingsDrawer from './FixSettingsDrawer';

interface EditAutoMergeFixesModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditAutoMergeFixesModal: React.FC<EditAutoMergeFixesModalProps> = (props: EditAutoMergeFixesModalProps) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const { mergeUserCreatedPRs, workingBranchUserCreatedPRMergeType, releaseBranchUserCreatedPRMergeType } =
        useSelector(selectFixRequestsAutoMergeUserCreatedPRsSettings(props.appId));

    const isMergeUserCreatedPRsEnabled = useWatch('mergeUserCreatedPRs', form);

    const initialValues = {
        mergeUserCreatedPRs: mergeUserCreatedPRs,
        workingBranchUserCreatedPRMergeType: workingBranchUserCreatedPRMergeType,
        releaseBranchUserCreatedPRMergeType: releaseBranchUserCreatedPRMergeType,
    };

    const onFinish = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            mergeUserCreatedPRs: value.mergeUserCreatedPRs,
            workingBranchUserCreatedPRMergeType: value.workingBranchUserCreatedPRMergeType,
            releaseBranchUserCreatedPRMergeType: value.releaseBranchUserCreatedPRMergeType,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    const displayMergeStrategyValue = (strategy: MergeType) => {
        switch (strategy) {
            case MergeType.DoNotMerge:
                return 'Do not merge user-created PRs';
            case MergeType.MergeCommit:
                return 'Merge user-created PRs with merge commit';
            case MergeType.Squash:
                return 'Squash merge user-created PRs';
        }
    };

    const mergeStrategyLabel = (targetBranch: string) => (
        <Text
            style={{
                color: isMergeUserCreatedPRsEnabled ? runwayColors.black : antGrey.grey5,
            }}
        >
            {targetBranch} branch
        </Text>
    );

    const mergeStrategyOptions = Object.values(MergeType).map((strategy) => {
        return (
            <Option value={strategy} key={strategy}>
                {displayMergeStrategyValue(strategy)}
            </Option>
        );
    });

    return (
        <FixSettingsDrawer
            title="Auto-merge fixes"
            open={props.open}
            loading={isLoading}
            onSave={form.submit}
            onCancel={props.onCancel}
            afterClose={afterClose}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    If enabled, user-created PRs with fixes against the selected target branches will be automatically
                    merged if they are approved and have no conflicts.
                </Text>
                <Form
                    onFinish={onFinish}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="mergeUserCreatedPRs"
                        valuePropName="checked"
                        label={'Auto-merge user-created PRs with fixes'}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item name="workingBranchUserCreatedPRMergeType" label={mergeStrategyLabel('Working')}>
                        <Select disabled={!isMergeUserCreatedPRsEnabled} style={{ width: '100%' }}>
                            {mergeStrategyOptions}
                        </Select>
                    </Form.Item>
                    <Form.Item name="releaseBranchUserCreatedPRMergeType" label={mergeStrategyLabel('Release')}>
                        <Select disabled={!isMergeUserCreatedPRsEnabled} style={{ width: '100%' }}>
                            {mergeStrategyOptions}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </FixSettingsDrawer>
    );
};

export default EditAutoMergeFixesModal;
